import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { Product, EnquiryForm as EnquiryFormType } from '../types';
import { submitEnquiry } from '../utils/api';
import './EnquiryForm.css';

interface EnquiryFormProps {
  product?: Product;
}

type Status = 'idle' | 'loading' | 'success' | 'error' | 'captcha';

const EMPTY_FORM: EnquiryFormType = { name: '', phone: '', email: '', message: '' };
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const PHONE_RE = /^\+?[0-9][0-9\s-]{7,18}$/;

const EnquiryForm: React.FC<EnquiryFormProps> = ({ product }) => {
  const [form,           setForm]           = useState<EnquiryFormType>(EMPTY_FORM);
  const [status,         setStatus]         = useState<Status>('idle');
  const [errorMessage,   setErrorMessage]   = useState<string>('');
  const [honeypot,       setHoneypot]       = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const openedAt = useRef(Date.now()); // when this form first became visible

  const set = (k: keyof EnquiryFormType, v: string): void => {
    setForm(f => ({ ...f, [k]: v }));
    if (status === 'error') setStatus('idle');
    setErrorMessage('');
  };

  const submit = async (): Promise<void> => {
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (name.length < 2 || name.length > 100) {
      setErrorMessage('Please enter a name between 2 and 100 characters.'); setStatus('error'); return;
    }
    if (!PHONE_RE.test(phone)) {
      setErrorMessage('Please enter a valid WhatsApp or phone number.'); setStatus('error'); return;
    }
    if (email.length > 254 || !EMAIL_RE.test(email)) {
      setErrorMessage('Please enter a valid email address.'); setStatus('error'); return;
    }
    if (form.message.length > 2000) {
      setErrorMessage('Your message must be 2,000 characters or fewer.'); setStatus('error'); return;
    }
    if (!recaptchaToken) { setStatus('captcha'); return; }
    setStatus('loading');
    try {
      await submitEnquiry(form, product?.name, product?.asin, recaptchaToken, honeypot, openedAt.current);
      setStatus('success');
      setForm(EMPTY_FORM);
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      const apiError = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data?.error
        : undefined;
      setErrorMessage(apiError || 'Unable to send your enquiry. Please try again later.');
      setStatus('error');
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  if (status === 'success') return (
    <div className="eq-success">
      <div className="eq-success-icon">✅</div>
      <h4>Enquiry Sent!</h4>
      <p>We'll contact you shortly. Thank you!</p>
      <button onClick={() => setStatus('idle')} className="btn-primary" style={{ marginTop: '12px' }}>
        Send Another
      </button>
    </div>
  );

  return (
    <div className="enquiry-form">
      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={e => setHoneypot(e.target.value)}
        style={{ display: 'none' }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="eq-grid">
        <div className="field">
          <label htmlFor="eq-name">Your Name *</label>
          <input id="eq-name" value={form.name} onChange={e => set('name', e.target.value)}
                 placeholder="Full name" required maxLength={100} autoComplete="name" />
        </div>
        <div className="field">
          <label htmlFor="eq-phone">WhatsApp / Phone *</label>
          <input id="eq-phone" value={form.phone} onChange={e => set('phone', e.target.value)}
                 placeholder="+91 xxxxx xxxxx" type="tel" required maxLength={20}
                 inputMode="tel" autoComplete="tel" />
        </div>
        <div className="field eq-full">
          <label htmlFor="eq-email">Email *</label>
          <input id="eq-email" value={form.email} onChange={e => set('email', e.target.value)}
                 placeholder="your@email.com" type="email" required maxLength={254} autoComplete="email" />
        </div>
        <div className="field eq-full">
          <label htmlFor="eq-message">Message</label>
          <textarea id="eq-message" value={form.message} onChange={e => set('message', e.target.value)}
                    placeholder="Any question about size, colour, delivery..." rows={3} maxLength={2000} />
        </div>
      </div>

      <div className="captcha-wrap">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={SITE_KEY}
          onChange={token => setRecaptchaToken(token)}
          onExpired={() => setRecaptchaToken(null)}
        />
      </div>

      {status === 'error' && (
        <p className="form-error" role="alert">{errorMessage || 'Please check the form and try again.'}</p>
      )}
      {status === 'captcha' && (
        <p className="form-error" role="alert">Please complete the CAPTCHA verification.</p>
      )}

      <button className="btn-primary btn-lg btn-block" onClick={submit} disabled={status === 'loading'}>
        {status === 'loading' ? 'Sending…' : 'Send Enquiry'}
      </button>
    </div>
  );
};

export default EnquiryForm;
